import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSlice } from "@bridge-applications/provider-kit";
import UserReason from "@userreason/js";
import { CLIENT_VERSION, DEVELOPMENT_TOOLS_ENABLED } from "../../state/config";
import { resolveSupportedLocale } from "../i18n/locales";
import { playerFeedbackContext } from "../helpers/playerFeedback";
import { playerFeedbackMessages } from "../helpers/playerFeedbackMessages";

/** Survey only after gameplay settles; native dialogs and background tabs defer it. */
export function usePlayerFeedback(
    completedSpinUserId: string | null,
    busy: boolean,
) {
    const { i18n } = useTranslation();
    const locale = resolveSupportedLocale(i18n.resolvedLanguage, i18n.language);
    const { state: auth } = useSlice("authSlice");
    const { state: dailyReward } = useSlice("dailyRewardSlice");
    const { state: slot } = useSlice("slotMachineSlice");
    const identifiedUser = useRef<string | null>(null);
    const authenticated = auth.bootstrapStatus === "ready" && auth.userId !== null;
    const telegramPlayer = auth.telegramUserId !== null;
    const dailyRewardClaimed = dailyReward.claimState === "claimed";
    const settledSpinsCount = slot.snapshot?.player.settledSpinsCount;
    const completedSpinThisVisit =
        auth.userId !== null && completedSpinUserId === auth.userId;
    const context = useMemo(
        () => playerFeedbackContext({
            authenticated,
            telegramPlayer,
            development: DEVELOPMENT_TOOLS_ENABLED,
            dailyRewardClaimed,
            settledSpinsCount,
            completedSpinThisVisit,
        }),
        [authenticated, telegramPlayer, dailyRewardClaimed,
            settledSpinsCount, completedSpinThisVisit],
    );

    useEffect(() => {
        // The app's selected language also controls the surrounding survey UI.
        UserReason.init({
            siteId: "3f66120f-736d-4381-8c12-2eb27c13d322",
            apiBaseUrl: "https://api-dev.userreason.com",
            waitForIdentity: true,
            locale,
            messages: playerFeedbackMessages(locale),
            appVersion: CLIENT_VERSION,
        });
        UserReason.setAvailable(false);
        identifiedUser.current = null;
        return () => {
            UserReason.destroy();
            identifiedUser.current = null;
        };
    }, [locale]);

    useEffect(() => {
        UserReason.setAvailable(false);
        if (!authenticated || !auth.userId || !context.livePlayer) {
            if (identifiedUser.current !== null) {
                UserReason.reset();
                identifiedUser.current = null;
            }
            return;
        }
        if (identifiedUser.current !== auth.userId) {
            UserReason.identify(auth.userId);
            identifiedUser.current = auth.userId;
        }
        UserReason.setContext(context);
        if (busy || (!context.dailyRewardClaimed && !context.completedSpinThisVisit)) return;

        let active = true;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let previousBlocked: boolean | undefined;
        const updateAvailability = () => {
            const blocked = document.visibilityState !== "visible" ||
                document.querySelector("dialog[open]") !== null;
            if (blocked === previousBlocked) return;
            previousBlocked = blocked;
            clearTimeout(timer);
            UserReason.setAvailable(false);
            if (blocked) return;
            timer = setTimeout(() => {
                if (!active || document.visibilityState !== "visible" ||
                    document.querySelector("dialog[open]")) return;
                UserReason.setAvailable(true);
                void UserReason.event("gameplay.idle").catch((error: unknown) => {
                    if (active) console.error("[PlayerFeedback] Survey opportunity failed", error);
                });
            }, 2000);
        };
        const observer = new MutationObserver(updateAvailability);
        observer.observe(document.body, {
            subtree: true, childList: true, attributes: true, attributeFilter: ["open"],
        });
        document.addEventListener("visibilitychange", updateAvailability);
        updateAvailability();
        return () => {
            active = false;
            clearTimeout(timer);
            observer.disconnect();
            document.removeEventListener("visibilitychange", updateAvailability);
            UserReason.setAvailable(false);
        };
    }, [authenticated, auth.userId, auth.sessionGeneration, busy, context, locale]);
}
